import { LocalizedText, showSuccessAlert, SubmitHandler, useFormControls } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    EnterpriseProfileUpdateParameters,
    useRepositoryMutation,
    UserProfileResult,
} from '@mews-commander/data-access';
import { useInvalidateUserProfile } from '../../hooks/useInvalidateUserProfile';
import { FieldNames } from '../FieldNames';
import { useEditUserFormProps } from './formProps';
import { getSubmittedUserEditValues } from './getSubmittedUserEditValues';
import { EditUserFormValues } from './schema';

export const useEditEnterpriseProfileForm = ({
    userProfile,
    onSuccess,
}: {
    userProfile: UserProfileResult;
    onSuccess: () => void;
}) => {
    const { resolver, defaultValues, isFetchingData } = useEditUserFormProps(userProfile);
    const invalidateUserProfile = useInvalidateUserProfile();

    const editEnterpriseProfileMutation = useRepositoryMutation('enterpriseProfiles', 'update', {
        onSuccess: ({ users }) => {
            if (users?.[0].id) {
                invalidateUserProfile(users[0].id);
            }
            onSuccess();

            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.UserEditSavedDescription}
                        substitutes={{ name: userProfile.fullName || '' }}
                    />
                ),
            });
        },
    });

    const { getOptionsValue, getAdminValue, getRoleIdsValue, getPrivilegesValue, getLearningPathValue } =
        getSubmittedUserEditValues();

    const handleSubmit: SubmitHandler<EditUserFormValues> = (values, { dirtyFields }) => {
        const data: EnterpriseProfileUpdateParameters = {
            enterpriseProfileId: userProfile.enterpriseProfileIds?.[0] || '',
            options: getOptionsValue(dirtyFields, values),
            isAdmin: getAdminValue(dirtyFields, values),
            roleIds: getRoleIdsValue(dirtyFields, values),
            privileges: getPrivilegesValue(dirtyFields, values),
            assignedLearningPath: getLearningPathValue(dirtyFields, values),
            departmentId: dirtyFields.departmentId ? { value: values[FieldNames.DepartmentId] } : undefined,
            superiorProfileId: dirtyFields.superiorProfileId
                ? { value: values[FieldNames.SuperiorProfileId] || null }
                : undefined,
            employeeIncentiveCode: dirtyFields.employeeIncentiveCode
                ? { value: values[FieldNames.EmployeeIncentiveCode] }
                : undefined,
        };

        void editEnterpriseProfileMutation.mutate(data);
    };

    const otherMethods = useFormControls({
        onSubmit: handleSubmit,
        resolver,
        defaultValues,
    });

    return {
        ...otherMethods,
        isPending: editEnterpriseProfileMutation.isPending || isFetchingData,
    };
};
